import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLatest } from '../../hooks/useLatest';
import { useSyncedMask } from '../../hooks/useSyncedMask';
import {
    absorbsLiteralPrefix,
    conformToMask,
    InputMaskPattern,
    InputMaskSlots,
    literalPrefixLength,
    parseInputMask,
} from './inputMask';

export interface IUseInputMaskOptions {
    mask: InputMaskPattern | undefined;
    /** Символ незаполненной позиции, `null` — позиции не показываются */
    slotChar: string | null;
    /** Поле принимает ввод; иначе только показывает значение по маске */
    editable: boolean;
    invalid?: boolean;
    /** Значение владельца; `undefined` — поле неуправляемое */
    value: string | undefined;
    /** Начальное значение неуправляемого поля */
    defaultValue?: string;
    /** Ввод пользователя — текст поля по маске; `false` — отклонить и вернуть прежний текст */
    onInput: (text: string, input: HTMLInputElement) => boolean | void;
}

export interface IUseInputMaskResult {
    active: boolean;
    /** Ref поля ввода; без маски не нужен */
    ref: ((node: HTMLInputElement | null) => void) | undefined;
    /** Начальный текст неуправляемого поля */
    initialText: string;
    /** Текст, пришедший мимо маски (событие change без input) */
    applyText: (text: string) => void;
}

interface IBulkText {
    text: string;
    caret: number;
}

/**
 * Вставка `inserted` на место `prev[start, tailFrom)`. Вставка в префикс маски заменяет его: префикс поглощается, только
 * если он есть в самом вставленном тексте.
 */
const bulkText = (
    prev: string,
    start: number,
    inserted: string,
    tailFrom: number,
    slots: InputMaskSlots,
): IBulkText => {
    const prefix = literalPrefixLength(slots);
    if (start > prefix) {
        const head = prev.slice(0, start) + inserted;
        return { text: conformToMask(head + prev.slice(tailFrom), slots), caret: conformToMask(head, slots).length };
    }
    const absorb = absorbsLiteralPrefix(inserted, slots);
    return {
        text: conformToMask(inserted + prev.slice(Math.max(prefix, tailFrom)), slots, absorb),
        caret: conformToMask(inserted, slots, absorb).length,
    };
};

/**
 * Маска поля на `useMask` (@mantine/hooks) через `useSyncedMask`. `useMask` сам пишет текст в поле, поэтому поле с
 * маской неуправляемое: ввод уходит владельцу через `onInput`, а его значение приходит в поле императивно.
 *
 * Вставку и вставку из автозаполнения (больше одного символа за раз) разбирает `conformToMask`: `useMask` раскладывает
 * такой текст только по позициям ввода, и `+7` из `+7 912 345-67-89` в маске `+7 999 999-99-99` уходило в номер.
 * Обработчики `useMask` на поле при этом отрабатывают вхолостую (без события владельцу), а событие всплывает дальше:
 * `onPaste` и слушатели выше его получают.
 */
export const useInputMask = ({
    mask,
    slotChar,
    editable,
    invalid,
    value,
    defaultValue,
    onInput,
}: IUseInputMaskOptions): IUseInputMaskResult => {
    const slots = useMemo<InputMaskSlots | null>(() => (mask ? parseInputMask(mask) : null), [mask]);
    const [input, setInput] = useState<HTMLInputElement | null>(null);
    const attached = !!slots && editable && !!input;

    const onInputRef = useLatest(onInput);
    const slotsRef = useLatest(slots);
    const attachedRef = useLatest(attached);
    const [initialText] = useState(() => {
        const initial = value ?? defaultValue ?? '';
        return slots ? conformToMask(initial, slots) : initial;
    });
    // Последний текст, о котором знают обе стороны: отданный владельцу или пришедший от него
    const knownRef = useRef<string | undefined>(value ?? (defaultValue === undefined ? undefined : initialText));
    // Текст вставки пишем мы сами: события useMask владельцу не нужны
    const silentRef = useRef(false);

    const {
        ref: attach,
        write,
        value: display,
    } = useSyncedMask({
        mask: slots ?? [],
        slotChar,
        invalid: !!invalid,
        onChangeRaw: (_raw, masked) => {
            if (input) emit(input, masked);
        },
    });
    const displayRef = useLatest(display);

    // Текст в поле без события для владельца: useMask разбирает его заново (с поглощением постоянных символов).
    // `userInput` — разбор вставки пользователя: её отменяет Ctrl+Z.
    const writeText = useCallback(
        (node: HTMLInputElement, text: string, userInput = false) => {
            const current = slotsRef.current;
            const next = current ? conformToMask(text, current) : text;
            if (attachedRef.current) write(next, userInput);
            else node.value = next;
        },
        [slotsRef, attachedRef, write],
    );

    const emit = useCallback(
        (node: HTMLInputElement, text: string) => {
            if (silentRef.current || text === knownRef.current) return;
            if (onInputRef.current(text, node) === false) {
                writeText(node, knownRef.current ?? '');
                return;
            }
            knownRef.current = text;
        },
        [onInputRef, writeText],
    );

    const applyText = (text: string) => {
        if (!input || !slots) return;
        writeText(input, text);
        emit(input, input.value);
    };

    useLayoutEffect(() => {
        if (!input || !slots || !editable) return undefined;
        attach(input);
        const host = input.parentElement ?? input;
        let pending: IBulkText | null = null;

        // Погружение на предке — раньше обработчиков useMask на поле: они отработают вхолостую
        const onPaste = (event: Event) => {
            if (event.target !== input) return;
            const inserted = (event as ClipboardEvent).clipboardData?.getData('text') ?? '';
            if (!inserted) return;
            const start = input.selectionStart ?? input.value.length;
            pending = bulkText(input.value, start, inserted, input.selectionEnd ?? start, slots);
            silentRef.current = true;
        };
        const onBulkInput = (event: Event) => {
            if (event.target !== input) return;
            const prev = displayRef.current;
            const curr = input.value;
            let start = 0;
            const maxStart = Math.min(prev.length, curr.length);
            while (start < maxStart && prev[start] === curr[start]) start++;
            let end = 0;
            const maxEnd = maxStart - start;
            while (end < maxEnd && prev[prev.length - 1 - end] === curr[curr.length - 1 - end]) end++;
            if (curr.length - end - start <= 1) return;
            pending = bulkText(prev, start, curr.slice(start, curr.length - end), prev.length - end, slots);
            silentRef.current = true;
        };
        // Всплытие на предке — после обработчиков useMask
        const onApply = (event: Event) => {
            if (event.target !== input || !pending) return;
            const { text, caret } = pending;
            pending = null;
            silentRef.current = false;
            writeText(input, text, true);
            emit(input, input.value);
            if (input.ownerDocument.activeElement === input) {
                const position = Math.min(caret, input.value.length);
                input.setSelectionRange(position, position);
            }
        };
        // useMask на уходе из поля убирает символы незаполненных позиций, не сообщая владельцу. Владелец узнаёт об этом,
        // если знал текст до ухода: своё значение в другом виде (`+7912`) он получать не должен
        let beforeBlur: string | null = null;
        const onBlur = (event: Event) => {
            if (event.target === input) beforeBlur = input.value;
        };
        const onFocusOut = (event: Event) => {
            if (event.target !== input) return;
            const before = beforeBlur;
            beforeBlur = null;
            if (before === knownRef.current && input.value !== before) emit(input, input.value);
        };
        host.addEventListener('paste', onPaste, true);
        host.addEventListener('input', onBulkInput, true);
        host.addEventListener('paste', onApply);
        host.addEventListener('input', onApply);
        host.addEventListener('blur', onBlur, true);
        host.addEventListener('focusout', onFocusOut);
        return () => {
            host.removeEventListener('paste', onPaste, true);
            host.removeEventListener('input', onBulkInput, true);
            host.removeEventListener('paste', onApply);
            host.removeEventListener('input', onApply);
            host.removeEventListener('blur', onBlur, true);
            host.removeEventListener('focusout', onFocusOut);
            silentRef.current = false;
            attach(null);
        };
    }, [input, slots, editable, attach, emit, writeText, displayRef]);

    useLayoutEffect(() => {
        if (!input || !slots || value === undefined || value === knownRef.current) return;
        knownRef.current = value;
        writeText(input, value);
    }, [input, slots, value, writeText]);

    useLayoutEffect(() => {
        if (input && slots && !editable) writeText(input, knownRef.current ?? input.value);
    }, [input, slots, editable, writeText]);

    return { active: !!slots, ref: slots ? setInput : undefined, initialText, applyText };
};
